import type { IWorkItemMoveFacts } from '@orn/global/assistant/workItemMove.js';

/**
 * The two states the line can report, which differ by glyph as well as words:
 * a clock while the work is still out, a check once it is done.
 */
export type MoveLineTone = 'waiting' | 'resolved';

export interface IMoveLine {
  text: string;
  tone: MoveLineTone;
}

/**
 * The gray line under a tool-call report stating what that call left the work
 * item as, or `null` when there is nothing worth a line. Composed from facts
 * frozen at call time — a record of what the call did, never re-read.
 *
 * No line names a date. The glyph beside it already reads as "later", and the
 * item's own row renders the due date itself.
 *
 * @param viewerUserId - whose seat the line is written from; an item on the
 *   viewer's own plate reads as their reminder, not as waiting on someone.
 */
export function workItemMoveLine(fact: IWorkItemMoveFacts, viewerUserId: string): IMoveLine | null {
  if (fact.archived) {
    return {
      text: fact.title === null ? 'Marked as resolved' : `Marked ${fact.title} as resolved`,
      tone: 'resolved',
    };
  }
  if (fact.assignee !== null && fact.assignee.userId !== viewerUserId) {
    // No name, no line: "waiting for a response" with nobody named says less
    // than the pending badge the item's own row already carries.
    return fact.assignee.displayName === null
      ? null
      : { text: `Waiting for response from ${fact.assignee.displayName}`, tone: 'waiting' };
  }
  if (fact.dueDate === null) {
    return null;
  }
  return fact.assignee === null
    ? { text: 'Checking back', tone: 'waiting' }
    : { text: 'Reminder set', tone: 'waiting' };
}
